import { TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usualVerdict, type Pace } from '../../domain/pace';
import { currencyExponent } from '../../domain/money';
import { formatDayMonth } from '../../i18n/format';
import { Amount, Section, useFormat } from '../../ui';
import './pace.css';

/**
 * Spending pace on Home (V1.5): the current month's projected end, against
 * the budget's target and its usual month. Only for the month in progress
 * (data: useMonthInsights).
 */
export function PaceSection({ pace }: { pace: Pace }) {
  const { t, i18n } = useTranslation();
  const { money, percent, currency } = useFormat();
  const whole = (minor: number) => money(minor, { whole: true });
  // Per day in whole units, rounded down: rounding up could promise more than is left.
  const unit = 10 ** currencyExponent(currency);
  const perDay = (minor: number) => whole(Math.floor(minor / unit) * unit);
  const monthEnd = formatDayMonth(`${pace.month}-${String(pace.daysInMonth).padStart(2, '0')}`, i18n.language);
  const target = pace.target;
  const verdict = usualVerdict(pace.vsUsual);
  const spent = Math.max(0, pace.spent);

  // One scale for the bar: the projection or the target, whichever is further.
  // A little room past the target, so its tick never sits on the bar's end and reads as the end.
  const scale = Math.max(pace.projected ?? spent + pace.billsDue, (target?.value ?? 0) * 1.04, 1);
  const share = (v: number) => `${(Math.max(0, v) / scale) * 100}%`;
  const over = target !== null && target.status !== 'under';

  return (
    <Section title={t('pace.title')} data-testid="pace">
      {pace.projected === null ? (
        <p className="pace-note">{t('pace.tooEarly')}</p>
      ) : (
        <p className="pace-headline">
          <span className="pace-headline-label">{t('pace.heading', { date: monthEnd })}</span>
          <Amount minor={pace.projected} size="xl" whole />
        </p>
      )}

      {/* Without a projection or a target there is nothing to measure the spending against: no bar. */}
      {(pace.projected !== null || target) && (
        <div
          className="pace-bar"
          role="img"
          aria-label={t('pace.barLabel', {
            spent: whole(spent),
            bills: whole(pace.billsDue),
            rest: whole(pace.everydayRest),
            target: target ? whole(target.value) : '—',
          })}
        >
          <span className="pace-track">
            <span className="pace-seg pace-seg-spent" style={{ width: share(spent) }} />
            {pace.billsDue > 0 && <span className="pace-seg pace-seg-bills" style={{ width: share(pace.billsDue) }} />}
            {pace.everydayRest > 0 && <span className="pace-seg pace-seg-rest" style={{ width: share(pace.everydayRest) }} />}
          </span>
          {target && <span className={over ? 'pace-target pace-target-over' : 'pace-target'} style={{ left: share(target.value) }} />}
        </div>
      )}

      <dl className="pace-parts num">
        <div>
          <dt>
            <span className="pace-swatch pace-seg-spent" aria-hidden />
            {t('pace.spent')}
          </dt>
          <dd>{whole(spent)}</dd>
        </div>
        {(pace.billsDueCount > 0 || pace.billsUnknownCount > 0) && (
          <div>
            <dt>
              <span className="pace-swatch pace-seg-bills" aria-hidden />
              {t('pace.bills', { count: pace.billsDueCount + pace.billsUnknownCount })}
              {pace.billsUnknownCount > 0 && <span className="pace-aside"> · {t('pace.billsUnknown', { count: pace.billsUnknownCount })}</span>}
            </dt>
            <dd>{whole(pace.billsDue)}</dd>
          </div>
        )}
        {pace.projected !== null && pace.daysLeft > 0 && (
          <div>
            <dt>
              <span className="pace-swatch pace-seg-rest" aria-hidden />
              {t('pace.rest', { count: pace.daysLeft })}
            </dt>
            <dd>{whole(pace.everydayRest)}</dd>
          </div>
        )}
      </dl>

      {target && (
        <p className={over ? 'pace-line pace-line-over' : 'pace-line'} data-testid="pace-target">
          {over && <TriangleAlert size={16} aria-hidden className="pace-icon" />}
          <span>
            {target.status === 'already'
              ? t('pace.targetAlready', { target: whole(target.value), over: whole(spent - target.value) })
              : target.status === 'over'
                ? t('pace.targetOver', { target: whole(target.value), over: whole(-(target.margin ?? 0)) })
                : target.perDay !== null
                  ? t('pace.targetUnder', { target: whole(target.value), left: whole(target.left), perDay: perDay(target.perDay) })
                  : t('pace.targetUnderNothingLeft', { target: whole(target.value) })}
          </span>
        </p>
      )}

      {verdict && pace.usual && (
        <p className="pace-line" data-testid="pace-usual">
          {verdict === 'same'
            ? t('pace.usualSame', { usual: whole(pace.usual.value), count: pace.usual.months })
            : t(verdict === 'more' ? 'pace.usualMore' : 'pace.usualLess', {
                percent: percent(Math.abs(pace.vsUsual!.ratio!), { signed: false, digits: 0 }),
                usual: whole(pace.usual.value),
                count: pace.usual.months,
              })}
        </p>
      )}
    </Section>
  );
}
